import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Box,
  Grid,
  Divider,
  Avatar,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { providersApi } from '../services/api';
import { locationApi } from '../services/locationApi';
import { useNavigate } from 'react-router-dom';

const VisibilityManagement = () => {
  const navigate = useNavigate();

  // Services Visibility state
  const [countries, setCountries] = useState([]);
  const [citiesByCountry, setCitiesByCountry] = useState({});
  const [visibilities, setVisibilities] = useState([]);
  const [visLoading, setVisLoading] = useState(true);
  const [visError, setVisError] = useState('');

  // Store Visibility state
  const [storeVisibilities, setStoreVisibilities] = useState([]);
  const [storeVisLoading, setStoreVisLoading] = useState(true);
  const [storeVisError, setStoreVisError] = useState('');

  useEffect(() => {
    const fetchLocationsAndVisibilities = async () => {
      try {
        setVisLoading(true);
        setVisError('');

        const countriesResp = await locationApi.getAllCountries();
        const countriesList = Array.isArray(countriesResp) ? countriesResp : [];
        const normalizedCountries = countriesList.map((c) => {
          if (typeof c === 'string') return { id: c, name: c };
          return { id: c.id ?? c.code ?? c.name, name: c.name ?? c.country ?? c.code ?? '' };
        }).filter(c => c.name);
        setCountries(normalizedCountries);

        const citiesEntries = await Promise.all(
          normalizedCountries.map(async (country) => {
            try {
              const resp = await locationApi.getCitiesByCountryId(country.id);
              const arr = Array.isArray(resp) ? resp : [];
              const cities = arr.map((ct) => (typeof ct === 'string' ? ct : (ct.name ?? ct.city ?? '')))
                               .filter(Boolean)
                               .sort((a, b) => a.localeCompare(b));
              return [country.name, cities];
            } catch (e) {
              return [country.name, []];
            }
          })
        );
        setCitiesByCountry(Object.fromEntries(citiesEntries));

        const visResp = await providersApi.listServicesVisibility();
        const visList = Array.isArray(visResp) ? visResp : [];
        setVisibilities(visList.map(v => ({
          country: v.country?.nameEn ?? (typeof v.country === 'string' ? v.country : null),
          city: v.city?.nameEn ?? (typeof v.city === 'string' ? v.city : null),
          enabled: !!v.enabled,
          live: v.live !== undefined ? !!v.live : true,
          providerCount: v.providerCount ?? 0,
        })));
      } catch (err) {
        setVisError('Failed to load visibility settings.');
      } finally {
        setVisLoading(false);
      }
    };
    fetchLocationsAndVisibilities();
  }, []);

  useEffect(() => {
    const fetchStoreVisibilities = async () => {
      try {
        setStoreVisLoading(true);
        setStoreVisError('');
        const storeVisResp = await providersApi.listStoreVisibility();
        const storeVisList = Array.isArray(storeVisResp) ? storeVisResp : [];
        setStoreVisibilities(storeVisList.map(v => ({
          country: v.country?.nameEn ?? (typeof v.country === 'string' ? v.country : null),
          enabled: !!v.enabled,
        })));
      } catch (err) {
        setStoreVisError('Failed to load store visibility settings.');
      } finally {
        setStoreVisLoading(false);
      }
    };
    fetchStoreVisibilities();
  }, []);

  const getStoreCountryEnabled = (countryName) => {
    const entry = storeVisibilities.find(v => v.country === countryName);
    return entry ? !!entry.enabled : false;
  };

  const setLocalStoreVisibility = (countryName, enabled) => {
    setStoreVisibilities(prev => {
      const next = [...prev];
      const idx = next.findIndex(v => v.country === countryName);
      if (idx >= 0) next[idx] = { ...next[idx], enabled };
      else next.push({ country: countryName, enabled });
      return next;
    });
  };

  const handleToggleStoreCountry = async (countryName, newEnabled) => {
    const prevEnabled = getStoreCountryEnabled(countryName);
    setLocalStoreVisibility(countryName, newEnabled);
    try {
      await providersApi.upsertStoreVisibility({ country: countryName, enabled: newEnabled });
    } catch (e) {
      setStoreVisError(`Failed to update store visibility for ${countryName}`);
      setLocalStoreVisibility(countryName, prevEnabled);
    }
  };

  const getCountryDefault = (countryName) => {
    const entry = visibilities.find(v => v.country === countryName && (v.city === null || v.city === undefined));
    return entry ? !!entry.enabled : false;
  };

  const getCityEnabled = (countryName, cityName) => {
    const specific = visibilities.find(v => v.country === countryName && v.city === cityName);
    if (specific) return !!specific.enabled;
    return getCountryDefault(countryName);
  };

  const getCityLive = (countryName, cityName) => {
    const specific = visibilities.find(v => v.country === countryName && v.city === cityName);
    if (specific) return specific.live !== undefined ? !!specific.live : true;
    const countryEntry = visibilities.find(v => v.country === countryName && v.city == null);
    return countryEntry?.live !== undefined ? !!countryEntry.live : true;
  };

  const getProviderCount = (countryName, cityName) => {
    const specific = visibilities.find(v => v.country === countryName && v.city === cityName);
    return specific?.providerCount ?? 0;
  };

  const setLocalVisibility = (countryName, cityName, enabled) => {
    setVisibilities(prev => {
      const next = [...prev];
      const idx = next.findIndex(v => v.country === countryName && ((cityName == null && (v.city == null)) || v.city === cityName));
      if (idx >= 0) next[idx] = { ...next[idx], enabled };
      else next.push({ country: countryName, city: cityName ?? null, enabled, live: true, providerCount: 0 });
      return next;
    });
  };

  const setLocalLive = (countryName, cityName, live) => {
    setVisibilities(prev => {
      const next = [...prev];
      const idx = next.findIndex(v => v.country === countryName && v.city === cityName);
      if (idx >= 0) next[idx] = { ...next[idx], live };
      else next.push({ country: countryName, city: cityName ?? null, enabled: false, live, providerCount: 0 });
      return next;
    });
  };

  const handleToggleCity = async (countryName, cityName, newEnabled) => {
    const prevEnabled = getCityEnabled(countryName, cityName);
    const currentLive = getCityLive(countryName, cityName);
    setLocalVisibility(countryName, cityName, newEnabled);
    try {
      await providersApi.upsertServicesVisibility({ country: countryName, city: cityName, enabled: newEnabled, live: currentLive });
    } catch (e) {
      setVisError(`Failed to update ${cityName} in ${countryName}`);
      setLocalVisibility(countryName, cityName, prevEnabled);
    }
  };

  const handleToggleCityLive = async (countryName, cityName, newLive) => {
    const prevLive = getCityLive(countryName, cityName);
    const currentEnabled = getCityEnabled(countryName, cityName);
    setLocalLive(countryName, cityName, newLive);
    try {
      await providersApi.upsertServicesVisibility({ country: countryName, city: cityName, enabled: currentEnabled, live: newLive });
    } catch (e) {
      setVisError(`Failed to update live status for ${cityName} in ${countryName}`);
      setLocalLive(countryName, cityName, prevLive);
    }
  };

  const areAllCountryCitiesEnabled = (countryName) => {
    const cities = citiesByCountry[countryName] || [];
    if (cities.length === 0) return getCountryDefault(countryName);
    return cities.every((city) => getCityEnabled(countryName, city));
  };

  const areAllCountryCitiesLive = (countryName) => {
    const cities = citiesByCountry[countryName] || [];
    if (cities.length === 0) {
      const entry = visibilities.find(v => v.country === countryName && (v.city === null || v.city === undefined));
      return entry ? (entry.live !== undefined ? !!entry.live : true) : true;
    }
    return cities.every((city) => getCityLive(countryName, city));
  };

  const handleToggleCountry = async (countryName, enableAll) => {
    const cities = citiesByCountry[countryName] || [];
    const prevVisibilities = [...visibilities];
    // Optimistic update: set country default and remove city-level overrides
    setVisibilities(prev => {
      let next = prev.filter(v => !(v.country === countryName && v.city != null));
      const idx = next.findIndex(v => v.country === countryName && v.city == null);
      if (idx >= 0) next[idx] = { ...next[idx], enabled: enableAll };
      else next.push({ country: countryName, city: null, enabled: enableAll, live: true, providerCount: 0 });
      return next;
    });
    try {
      await providersApi.bulkSetCountryVisibility({ country: countryName, enabled: enableAll });
    } catch (e) {
      setVisError(`Failed to update country ${countryName}`);
      setVisibilities(prevVisibilities);
    }
  };

  const handleToggleCountryLive = async (countryName, liveAll) => {
    const prevVisibilities = [...visibilities];
    const countryDefaultEnabled = getCountryDefault(countryName);
    // Optimistic update: flip live on every row for the country, preserving enabled.
    setVisibilities(prev => {
      const next = prev.map(v => (v.country === countryName ? { ...v, live: liveAll } : v));
      const hasCountryDefault = next.some(v => v.country === countryName && (v.city === null || v.city === undefined));
      if (!hasCountryDefault) {
        next.push({ country: countryName, city: null, enabled: countryDefaultEnabled, live: liveAll, providerCount: 0 });
      }
      return next;
    });
    try {
      await providersApi.bulkSetCountryLive({ country: countryName, live: liveAll });
    } catch (e) {
      setVisError(`Failed to update live status for all cities in ${countryName}`);
      setVisibilities(prevVisibilities);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/providers')} sx={{ mb: 2 }}>
        Back to Providers
      </Button>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Visibility Settings
      </Typography>

      {/* Store Visibility Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Store Visibility by Country
        </Typography>
        {storeVisError && <Alert severity="error" sx={{ mb: 2 }}>{storeVisError}</Alert>}
        {storeVisLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {countries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No countries found.</Typography>
            ) : (
              <Grid container spacing={2}>
                {countries.map((country) => {
                  const enabled = getStoreCountryEnabled(country.name);
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={`store-${country.name}`}>
                      <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'secondary.main', width: 28, height: 28 }}>
                            {country.name?.[0]?.toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {country.name}
                          </Typography>
                        </Box>
                        <Switch
                          size="small"
                          color={enabled ? 'success' : 'default'}
                          checked={enabled}
                          onChange={(e) => handleToggleStoreCountry(country.name, e.target.checked)}
                        />
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}
      </Box>

      {/* Services Visibility Section */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Services Visibility by Country and City
        </Typography>
        {visError && <Alert severity="error" sx={{ mb: 2 }}>{visError}</Alert>}
        {visLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {countries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No countries found.</Typography>
            ) : (
              countries.map((country) => (
                <Paper key={country.name} sx={{ p: 2, mb: 2 }} variant="outlined">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28 }}>
                        {country.name?.[0]?.toUpperCase()}
                      </Avatar>
                      <Typography variant="subtitle1">{country.name}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={areAllCountryCitiesEnabled(country.name)}
                            onChange={(e) => handleToggleCountry(country.name, e.target.checked)}
                          />
                        }
                        label={areAllCountryCitiesEnabled(country.name) ? 'All cities visible' : 'All cities hidden'}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            color="warning"
                            checked={areAllCountryCitiesLive(country.name)}
                            onChange={(e) => handleToggleCountryLive(country.name, e.target.checked)}
                          />
                        }
                        label={areAllCountryCitiesLive(country.name) ? 'All cities live' : 'All cities not live'}
                      />
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={1}>
                    {(citiesByCountry[country.name] || []).map((city) => {
                      const enabled = getCityEnabled(country.name, city);
                      const live = getCityLive(country.name, city);
                      const providerCount = getProviderCount(country.name, city);
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={`${country.name}-${city}`}>
                          <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1, pr: 1 }}>{city}</Typography>
                              <Chip
                                label={`${providerCount} providers`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 18, flexShrink: 0 }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    color={enabled ? 'success' : 'default'}
                                    checked={enabled}
                                    onChange={(e) => handleToggleCity(country.name, city, e.target.checked)}
                                  />
                                }
                                label={<Typography variant="caption">Visible</Typography>}
                                labelPlacement="end"
                                sx={{ mr: 0 }}
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    color={live ? 'warning' : 'default'}
                                    checked={live}
                                    disabled={!enabled}
                                    onChange={(e) => handleToggleCityLive(country.name, city, e.target.checked)}
                                  />
                                }
                                label={<Typography variant="caption">Live</Typography>}
                                labelPlacement="end"
                                sx={{ mr: 0 }}
                              />
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                    {(!citiesByCountry[country.name] || citiesByCountry[country.name].length === 0) && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">No cities listed for this country.</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              ))
            )}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default VisibilityManagement;
